import React, { useState, useEffect } from 'react';
import { array, bool, func, object, string } from 'prop-types';

import { connect } from 'react-redux';

import { get, isEmpty, isNil } from 'lodash';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import clsx from 'clsx';

import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';

import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';

import Button from '@material-ui/core/Button';
import GoogleLogo from '../../../components/GoogleLogo';
import BobConfirmButton from './../../../components/BobConfirmButton';
import BobLoadingMask from '../../../components/BobLoadingMask';

import * as ArchitectActions from '../../../actions/architectActions';
import * as ApplicationMessageActions from '../../../actions/applicationMessageActions';
import * as ContactActions from '../../../actions/contactActions';
import * as UiActions from '../../../actions/uiActions';

import classes from '../CreateEntityModal.module.css';

const ValidationSchema = Yup.object().shape({
    clientName: Yup.string()
        .max(45)
        .required(),
    businessType: Yup.string()
        .required(),
    primaryContact: Yup.string(),
    addressSearch:  Yup.string()
        .required()
});

const EditClientModal = ({
    bobLoading,
    businessTypes,
    onClose,
    clearErrors,
    client,
    clientContacts,
    contacts,
    open,
    title,
    isLoading,
    errors,
    fetchClient,
    fetchClientContacts,
    fetchClients,
    fetchClientNewOrUpdated,
    fetchContacts,
    selectedNode,
    toggleLoading,
    targetNavCompany,
    ...props
}) => {
    const [address, setAddress] = useState({});

    const [address1, setAddress1] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zip, setZip] = useState('');

    useEffect(() => {
        fetchClient(client.clientId);

        if (process.env.contactsEnabled) {
            fetchClientContacts(client.clientId);
        } else {
            fetchContacts(client.clientId);
        }
    }, [client]);

    const formik = useFormik({
        initialValues: {
            clientName:     get(selectedNode, 'name', ''),
            businessType:   get(selectedNode, 'businessType.id', ''),
            primaryContact: get(selectedNode, 'primaryContact.id', ''),
            addressSearch:  get(selectedNode, 'address.formattedAddress', '')
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                updateClient
            } = props;

            let isUpdated;

            const body = {
                name:             values.clientName,
                address,
                businessTypeId:   values.businessType,
                primaryContactId: values.primaryContact 
            };

            clearErrors();
            setSubmitting(true);

            try {
                if (!isLoading) {
                    toggleLoading(true);
                    const companyInfo = {
                        companyId: targetNavCompany.id
                    };
                    isUpdated = await updateClient(companyInfo, selectedNode.id, body);
                }

                if (isUpdated) {
                    body.id = selectedNode.id;
                    handleClose(body);
                } else {
                    setSubmitting(false);
                    toggleLoading(false);
                }
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const handleGeocodeAddress = async address => {
        try {
            const results = await geocodeByAddress(address);
            const latLng = await getLatLng(results[0]);

            const streetNumber = results[0].address_components.find(result => {
                return result.types.includes('street_number');
            });

            const route = results[0].address_components.find(result => {
                return result.types.includes('route');
            });

            const city = results[0].address_components.find(result => {
                return result.types.includes('locality');
            });

            const subLocality = results[0].address_components.find(result => {
                return result.types.includes('sublocality');
            });

            const administrativeAreaLevel2 = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_2');
            });

            const administrativeAreaLevel3 = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_3');
            });

            const state = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_1');
            });

            const zip = results[0].address_components.find(result => {
                return result.types.includes('postal_code');
            });

            const country = results[0].address_components.find(result => {
                return result.types.includes('country');
            });

            const address1Check = streetNumber ? `${streetNumber.long_name} ${route.long_name}` : route.long_name;
            const cityCheck = city ? city.long_name : administrativeAreaLevel3 ? administrativeAreaLevel3.long_name  : subLocality ? subLocality.long_name : '';

            formik.setFieldValue('addressSearch', results[0].formatted_address);

            setAddress1(address1Check);
            setCity(cityCheck);
            setState(state.short_name);
            setZip(zip.long_name);

            setAddress({
                latitude:                 get(latLng, 'lat', ''),
                longitude:                get(latLng, 'lng', ''),
                administrativeAreaLevel1: get(state, 'short_name', ''),
                administrativeAreaLevel2: get(administrativeAreaLevel2, 'long_name', ''),
                country:                  get(country, 'short_name', ''),
                locality:                 cityCheck,
                postalCode:               get(zip, 'long_name', ''),
                route:                    get(route, 'long_name', ''),
                streetNumber:             streetNumber ? streetNumber.long_name : '',
                formattedAddress:         get(results[0], 'formatted_address', '')
            });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Error: ', error);
        }
    };

    const handleClose = (body) => {
        toggleLoading(false);
        clearErrors();
        fetchClients(body);
        onClose();
    };

    if (!isEmpty(formik.values.addressSearch) && isEmpty(address1)) {
        handleGeocodeAddress(formik.values.addressSearch);
    }

    if (isNil(targetNavCompany.name)) {
        return null;
    }

    return (
        <React.Fragment>
            <div className={classes.inputs}>
                <FormControl className={classes.inputWrapper}>
                    <TextField
                        id="clientName"
                        name="clientName"
                        type="text"
                        value={formik.values.clientName}
                        label={<span>Client Name<span className={classes.required}> *</span></span>}
                        onChange={formik.handleChange}
                        InputLabelProps={{
                            classes: {
                                error:   classes.error,
                                focused: classes.labelFocused,
                                root:    classes.label
                            }
                        }}
                        InputProps={{
                            onBlur:  formik.handleBlur,
                            classes: {
                                error:     classes.cssErrorUnderline,
                                underline: classes.cssUnderline,
                                root:      classes.input
                            }
                        }}
                        FormHelperTextProps={{
                            classes: {
                                error: classes.error
                            }
                        }}
                        error={Boolean(formik.errors.clientName) && Boolean(formik.touched.clientName)}
                        helperText={Boolean(formik.touched.clientName) && formik.errors.clientName}
                    />
                </FormControl>
                <FormControl className={classes.inputWrapper}>
                    <InputLabel
                        htmlFor="businessType"
                        classes={{
                            error:   classes.error,
                            focused: classes.labelFocused,
                            root:    classes.label
                        }}
                        error={Boolean(formik.errors.businessType) && Boolean(formik.touched.businessType)}
                    >
                        {<span>Business Type<span className={classes.required}> *</span></span>}
                    </InputLabel>
                    <Select
                        value={formik.values.businessType}
                        id="businessType"
                        onChange={formik.handleChange}
                        name="businessType"
                        classes={{
                            root: classes.input
                        }}
                        inputProps={{
                            onBlur: formik.handleBlur
                        }}
                        error={Boolean(formik.errors.businessType) && Boolean(formik.touched.businessType)}
                    >
                        {
                            businessTypes.map(type =>
                                <MenuItem
                                    key={type.id}
                                    value={type.id}
                                >
                                    {type.name}
                                </MenuItem>
                            )
                        }
                    </Select>
                    {formik.touched.businessType &&
                        formik.errors.businessType && <FormHelperText className={classes.error}>{formik.errors.businessType}</FormHelperText>}
                </FormControl>
                { !isNil(client) ? 
                    <FormControl className={classes.inputWrapper}>
                        <InputLabel
                            htmlFor="primaryContact"
                            classes={{
                                error:   classes.error,
                                focused: classes.labelFocused,
                                root:    classes.label
                            }}
                            error={Boolean(formik.errors.primaryContact) && Boolean(formik.touched.primaryContact)}
                        >
                            {<span>Primary Contact</span>}
                        </InputLabel>
                        <Select
                            value={formik.values.primaryContact}
                            id="primaryContact"
                            onChange={formik.handleChange}
                            name="primaryContact"
                            classes={{
                                root: classes.input
                            }}
                            inputProps={{
                                onBlur: formik.handleBlur
                            }}
                            error={Boolean(formik.errors.primaryContact) && Boolean(formik.touched.primaryContact)}
                        >
                            {
                                process.env.contactsEnabled ?
                                    clientContacts.map(contact =>
                                        <MenuItem
                                            key={contact.id}
                                            value={contact.id}
                                        >
                                            {`${contact.userName.FirstName} ${contact.userName.LastName}`}
                                        </MenuItem>
                                    )
                                    :
                                    contacts.map(contact =>
                                        <MenuItem
                                            key={contact.id}
                                            value={contact.id}
                                        >
                                            {contact.name}
                                        </MenuItem>
                                    )
                            }
                        </Select>
                        {formik.touched.primaryContact &&
                            formik.errors.primaryContact && <FormHelperText className={classes.error}>{formik.errors.primaryContact}</FormHelperText>}
                    </FormControl>
                    : null 
                }
                <PlacesAutocomplete
                    value={formik.values.addressSearch}
                    onChange={address => formik.setFieldValue('addressSearch', address)}
                    onSelect={(handleGeocodeAddress)}
                >
                    {({ getInputProps, suggestions, getSuggestionItemProps }) => (
                        <React.Fragment>
                            <FormControl className={classes.inputWrapper}>
                                <InputLabel
                                    htmlFor="addressSearch"
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.addressSearch) && Boolean(formik.touched.addressSearch)}
                                >
                                    {<span>Search for Address<span className={classes.required}> *</span></span>}
                                </InputLabel>
                                <Input
                                    {...getInputProps({
                                        name: 'addressSearch',
                                        id:   'addressSearch'
                                    })}
                                    classes={{
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }}
                                    error={Boolean(formik.errors.addressSearch) && Boolean(formik.touched.addressSearch)}
                                />
                                {formik.touched.addressSearch &&
                                    formik.errors.addressSearch && <FormHelperText className={classes.error}>{formik.errors.addressSearch}</FormHelperText>}
                            </FormControl>
                            {suggestions.length > 0 && <Paper className={classes.autoCompleteList}>
                                <List>
                                    {suggestions.map((suggestion, index) => {
                                        return (
                                            <div key={index}>
                                                <ListItem
                                                    button
                                                    className={classes.autoCompleteListItem}
                                                    {...getSuggestionItemProps(suggestion)}
                                                >
                                                    <ListItemText primary={suggestion.description} />
                                                </ListItem>
                                            </div>
                                        );
                                    })}
                                </List>
                                <GoogleLogo />
                            </Paper>}
                        </React.Fragment>
                    )}
                </PlacesAutocomplete>
                <FormControl className={classes.inputWrapper}>
                    <TextField
                        id="address1"
                        name="address1"
                        type="text"
                        value={address1}
                        label="Address"
                        disabled
                        InputLabelProps={{
                            classes: {
                                error:   classes.error,
                                focused: classes.labelFocused,
                                root:    classes.label
                            }
                        }}
                        InputProps={{
                            classes: {
                                error:     classes.cssErrorUnderline,
                                underline: classes.cssUnderline,
                                root:      classes.input
                            }
                        }}
                    />
                </FormControl>
                <FormControl className={classes.inputWrapper}>
                    <TextField
                        id="city"
                        name="city"
                        type="text"
                        value={city}
                        label="City"
                        disabled
                        InputLabelProps={{
                            classes: {
                                error:   classes.error,
                                focused: classes.labelFocused,
                                root:    classes.label
                            }
                        }}
                        InputProps={{
                            classes: {
                                error:     classes.cssErrorUnderline,
                                underline: classes.cssUnderline,
                                root:      classes.input
                            }
                        }}
                    />
                </FormControl>
                <div className={classes.formRow}>
                    <FormControl className={clsx(classes.inputWrapper, classes.state)}>
                        <TextField
                            id="state"
                            name="state"
                            type="text"
                            value={state}
                            label="State"
                            disabled
                            InputLabelProps={{
                                classes: {
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }
                            }}
                            InputProps={{
                                classes: {
                                    error:     classes.cssErrorUnderline,
                                    underline: classes.cssUnderline,
                                    root:      classes.input
                                }
                            }}
                        />
                    </FormControl>
                    <FormControl className={clsx(classes.inputWrapper, classes.zip)}>
                        <TextField
                            id="zip"
                            name="zip"
                            type="text"
                            value={zip}
                            label="Zip Code"
                            disabled
                            InputLabelProps={{
                                classes: {
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }
                            }}
                            InputProps={{
                                classes: {
                                    error:     classes.cssErrorUnderline,
                                    underline: classes.cssUnderline,
                                    root:      classes.input
                                }
                            }}
                        />
                    </FormControl>
                </div>
            </div>
            <React.Fragment>
                <Button
                    className={classes.Button}
                    onClick={onClose}
                    variant="contained"
                >
                    Cancel
                </Button>
                <BobConfirmButton
                    className={classes.ButtonWithMargin}
                    disabled={formik.isSubmitting || isLoading}
                    isLoading={isLoading}
                    label={'Update'}
                    onClick={formik.handleSubmit}
                />
            </React.Fragment>
            {
                bobLoading.includes('fetchClient') ?
                    <BobLoadingMask />
                    :
                    null
            }
        </React.Fragment>
    );
};

EditClientModal.defaultProps = {
    businessTypes:    [],
    onClose:          () => {},
    contacts:         [],
    errors:           [],
    isLoading:        false,
    title:            '',
    targetNavCompany: {}
};

EditClientModal.propTypes = {
    businessTypes:    array,
    onClose:          func,
    contacts:         array,
    errors:           array,
    isLoading:        bool,
    title:            string,
    targetNavCompany: object
};

const mapStateToProps = state => {
    return {
        bobLoading:       state.bob.bobLoading,
        businessTypes:    state.architect.businessTypes,
        clientContacts:   state.contact.clientContacts,
        contacts:         state.architect.contacts,
        errors:           state.messages.errors,
        isLoading:        state.ui.isLoading,
        selectedNode:     state.architect.selectedNode,
        treeReset:        state.architect.treeReset,
        targetNavCompany: state.bob.targetNavCompany,
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...ApplicationMessageActions,
    ...ContactActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(EditClientModal);
